import * as React from 'react'
import { X } from 'lucide-react'
import { IconButton } from '@/components/ui/icon-button'
import { useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'

/*
 * SlideOver: a right-hand panel for editing a record from a list, on the
 * native modal <dialog> so focus is trapped and restored by the platform.
 * ×, Esc and the backdrop all close it through the unsaved-changes guard
 * (VIS-321): with unsaved changes in the panel they ask first. The footer
 * holds the panel's SaveBar, or a Close button when nothing has changed; a
 * one-shot panel puts its actions in a SlideOverFooter.
 */
function SlideOver({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean
  title: string
  /** Called once closing is confirmed, or at once when nothing is unsaved. */
  onClose: () => void
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  const t = useT()
  const { confirmThen } = useUnsavedChanges()
  const ref = React.useRef<HTMLDialogElement>(null)
  const opener = React.useRef<HTMLElement | null>(null)
  const titleId = React.useId()

  React.useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
      if (opener.current?.isConnected) opener.current.focus()
      opener.current = null
    }
  }, [open])

  const requestClose = () => confirmThen({ kind: 'close' }, onClose)

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault()
        requestClose()
      }}
      onClick={(event) => {
        if (event.target === ref.current) requestClose()
      }}
      className="m-0 ml-auto h-dvh max-h-dvh w-[min(36rem,100vw)] max-w-none border-l border-line bg-surface p-0 text-fg shadow-e3 backdrop:bg-[rgb(8_19_14_/_45%)]"
    >
      {open ? (
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between gap-2 border-b border-line px-5 py-3">
            <h2 id={titleId} className="text-lg font-semibold text-fg-strong">
              {title}
            </h2>
            <IconButton label={t('forms.slideOver.close')} onClick={requestClose}>
              <X />
            </IconButton>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
          {footer}
        </div>
      ) : null}
    </dialog>
  )
}

/*
 * SlideOverFooter: the action footer of a one-shot panel (Cancel, then one
 * primary action), for panels that act rather than save. Focus order follows
 * reading order: Cancel, then the primary. Panels that save use SaveBar
 * docked="panel" instead.
 */
function SlideOverFooter({ children }: { children: React.ReactNode }) {
  return (
    <div data-slot="slide-over-footer" className="flex flex-wrap justify-end gap-2 border-t border-line bg-surface px-4 py-3">
      {children}
    </div>
  )
}

export { SlideOver, SlideOverFooter }
